import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { CheckCircle, RotateCcw, Clock, Volume2, Settings, ChevronLeft, ChevronRight, Plus, RefreshCw, BookOpen, Timer, Trash2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';

import VerseReflectionModal from '../verse-library/components/VerseReflectionModal';
import { verseService, activityService } from '../../services/supabaseService';
import { AuthContext } from '../../contexts/AuthContext';
import { recordReviewSession, updateStreakWithFreezeCheck } from '../../services/awardService';
import { useAwardToast } from '../../contexts/AwardToastContext';
import useTouchSwipe, { triggerHaptic } from '../../hooks/useTouchSwipe';
import { trackDailyReviewStarted, trackDailyReviewCompleted } from '../../utils/ga';
import { DailyReviewSkeleton, FadeSlideIn } from '../../components/ui/SkeletonLoader';


// ── Confetti particle component ──────────────────────────────────────────────
const ConfettiParticle = ({ style }) => (
  <div className="absolute rounded-sm pointer-events-none" style={style} />
);

const CelebrationOverlay = ({ onDone }) => {
  const colors = ['#10B981','#F59E0B','#3B82F6','#EC4899','#8B5CF6','#EF4444','#06B6D4'];
  const particles = Array.from({ length: 60 }, (_, i) => {
    const color = colors?.[i % colors?.length];
    const left = `${Math.random() * 100}%`;
    const delay = `${Math.random() * 0.8}s`;
    const duration = `${0.8 + Math.random() * 0.8}s`;
    const size = `${6 + Math.random() * 8}px`;
    return {
      key: i,
      style: {
        left,
        top: '-10px',
        width: size,
        height: size,
        backgroundColor: color,
        animation: `confettiFall ${duration} ${delay} ease-in forwards`,
        transform: `rotate(${Math.random() * 360}deg)`,
      }
    };
  });

  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="fixed inset-0 z-50 pointer-events-none overflow-hidden">
      {particles?.map(p => <ConfettiParticle key={p?.key} style={p?.style} />)}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-surface/95 border border-border rounded-2xl px-8 py-6 text-center shadow-2xl animate-bounce-in">
          <div className="text-5xl mb-3">🎉</div>
          <h2 className="text-2xl font-bold text-foreground mb-1">Session Complete!</h2>
          <p className="text-muted-foreground text-sm">Outstanding work! Keep it up!</p>
        </div>
      </div>
    </div>
  );
};
// ── End Confetti ─────────────────────────────────────────────────────────────

const DailyReviewSystem = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // ── Local-timezone date helpers ──────────────────────────────────────────
  // Returns 'YYYY-MM-DD' in the user's LOCAL timezone (not UTC).
  // This ensures midnight resets align with the user's clock, not the server's.
  const getLocalDateString = (date = new Date()) => {
    const year = date?.getFullYear();
    const month = String(date?.getMonth() + 1)?.padStart(2, '0');
    const day = String(date?.getDate())?.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getTomorrowLocalDateString = () => {
    const tomorrow = new Date();
    tomorrow?.setDate(tomorrow?.getDate() + 1);
    return getLocalDateString(tomorrow);
  };

  // Check if we arrived from verse-library with pre-selected extra review verses
  const incomingExtraReview = location?.state?.isExtraReview && location?.state?.extraReviewVerses?.length > 0;
  const incomingVerses = location?.state?.extraReviewVerses || [];
  const incomingReschedule = location?.state?.rescheduleOnResults !== false; // default true
  const incomingUpdateLastReviewed = location?.state?.updateLastReviewed !== false; // default true

  const [currentVerseIndex, setCurrentVerseIndex] = useState(0);
  const [todaysReview, setTodaysReview] = useState([]);
  const [reviewSession, setReviewSession] = useState({
    completed: [],
    correct: 0,
    incorrect: 0,
    hard: 0,
    missed: 0,
    totalDifficulty: 0,
    missedVerses: [],
    pendingUpdates: []
  });
  // Track per-card answers: { [index]: { response, timeSpent } }
  const [cardAnswers, setCardAnswers] = useState({});
  const [showMemorization, setShowMemorization] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [showVerse, setShowVerse] = useState(false);
  const [bulkReview, setBulkReview] = useState(false);
  const [extraPracticeMode, setExtraPracticeMode] = useState(incomingExtraReview);
  const [extraReviewReschedule, setExtraReviewReschedule] = useState(incomingReschedule);
  const [extraReviewUpdateLastReviewed, setExtraReviewUpdateLastReviewed] = useState(incomingUpdateLastReviewed);
  const [dailyReviewCompleted, setDailyReviewCompleted] = useState(() => {
    const today = getLocalDateString();
    return localStorage.getItem('lastDailyReviewCompleted') === today;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [memorizationStats, setMemorizationStats] = useState(null);
  const [settings, setSettings] = useState({
    maxDailyVerses: 30,
    audioEnabled: true,
    autoAdvance: false
  });
  const [isReflectionModalOpen, setIsReflectionModalOpen] = useState(false);
  const [selectedVerseForReflection, setSelectedVerseForReflection] = useState(null);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [phase0Verses, setPhase0Verses] = useState([]);

  // ── Streak freeze state ───────────────────────────────────────────────────
  const [streakFreezeResult, setStreakFreezeResult] = useState(null); // { streakPreserved, goldSpent, newStreak }
  const [showStreakFreezeNotice, setShowStreakFreezeNotice] = useState(false);

  // ── Swipe state for visual feedback ──────────────────────────────────────
  const [swipeHint, setSwipeHint] = useState(null); // 'left' | 'right' | 'up' | null

  // ── Card animation state ──────────────────────────────────────────────────
  const [cardAnimation, setCardAnimation] = useState(null); // 'shake' | 'glow' | null
  const [showCelebration, setShowCelebration] = useState(false);

  // ── Delete card state ─────────────────────────────────────────────────────
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // ── Timer state ──────────────────────────────────────────────────────────
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [sessionElapsed, setSessionElapsed] = useState(0); // seconds
  const [cardStartTime, setCardStartTime] = useState(null);
  const [cardTimes, setCardTimes] = useState([]); // seconds per card
  const timerRef = useRef(null);

  // ── Session-level progress state (updates after each answer) ─────────────
  const [sessionProgress, setSessionProgress] = useState({
    reviewed: 0,
    correct: 0,
    incorrect: 0,
    hard: 0,
  });

  const { showAwardToast } = useAwardToast();

  // Use a ref so swipe handlers always call the latest handleResponse
  const handleResponseRef = useRef(null);

  // Swipe handlers — defined here (before any early returns) to satisfy Rules of Hooks
  const handleSwipeLeft = useCallback(() => {
    setSwipeHint('left');
    setTimeout(() => { setSwipeHint(null); handleResponseRef?.current?.('missed'); }, 300);
  }, []);

  const handleSwipeRight = useCallback(() => {
    setSwipeHint('right');
    setTimeout(() => { setSwipeHint(null); handleResponseRef?.current?.('good'); }, 300);
  }, []);

  const handleCardTap = useCallback(() => {
    if (!showVerse && !sessionComplete) {
      triggerHaptic('light');
      setShowVerse(true);
    }
  }, [showVerse, sessionComplete]);

  const { handlers: swipeHandlers } = useTouchSwipe({
    onSwipeLeft: handleSwipeLeft,
    onSwipeRight: handleSwipeRight,
    threshold: 55,
  });

  // Text size state — shared with Bible Reader via localStorage
  const TEXT_SIZES = [
    { key: 'sm', label: 'S', px: '11px' },
    { key: 'base', label: 'M', px: '13px' },
    { key: 'lg', label: 'L', px: '16px' },
    { key: 'xl', label: 'XL', px: '20px' },
  ];
  const [textSize, setTextSize] = useState(() => localStorage.getItem('bibleReaderTextSize') || 'base');
  const getTextSizePx = () => TEXT_SIZES?.find(s => s?.key === textSize)?.px || '13px';
  const handleTextSizeChange = (key) => {
    setTextSize(key);
    localStorage.setItem('bibleReaderTextSize', key);
  };
  const currentTextSizeIndex = TEXT_SIZES?.findIndex(s => s?.key === textSize);
  const handleTextSizeDecrease = () => {
    if (currentTextSizeIndex > 0) handleTextSizeChange(TEXT_SIZES?.[currentTextSizeIndex - 1]?.key);
  };
  const handleTextSizeIncrease = () => {
    if (currentTextSizeIndex < TEXT_SIZES?.length - 1) handleTextSizeChange(TEXT_SIZES?.[currentTextSizeIndex + 1]?.key);
  };
  const textSizeScaleMap = { sm: '0.75rem', base: '1rem', lg: '1.25rem', xl: '1.5rem' };

  // ── Delete verse handler ──────────────────────────────────────────────────
  const handleDeleteVerse = async () => {
    const currentVerse = todaysReview?.[currentVerseIndex];
    if (!currentVerse || !user?.id) return;
    setIsDeleting(true);
    try {
      await verseService?.deleteVerse(currentVerse?.id);
      // Remove verse from session list
      const newReview = todaysReview?.filter((_, i) => i !== currentVerseIndex);
      setTodaysReview(newReview);
      setShowDeleteConfirm(false);
      // Advance to next verse or end session
      if (newReview?.length === 0) {
        stopSessionTimer();
        setSessionComplete(true);
      } else {
        const nextIndex = Math.min(currentVerseIndex, newReview?.length - 1);
        setCurrentVerseIndex(nextIndex);
        setShowVerse(false);
        setCardStartTime(Date.now());
        // Rebuild cardAnswers with shifted indices
        const newCardAnswers = {};
        Object.entries(cardAnswers)?.forEach(([idx, val]) => {
          const i = parseInt(idx);
          if (i < currentVerseIndex) newCardAnswers[i] = val;
          else if (i > currentVerseIndex) newCardAnswers[i - 1] = val;
        });
        setCardAnswers(newCardAnswers);
      }
    } catch (err) {
      console.error('Error deleting verse:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Format KJV text with special markup: [] for italics, ‹› for red text
  const formatKJVText = (text) => {
    if (!text) return text;
    const parts = [];
    let lastIndex = 0;
    const markupRegex = /\[([^\]]+)\]|‹([^›]+)›/g;
    let match;
    while ((match = markupRegex?.exec(text)) !== null) {
      if (match?.index > lastIndex) parts?.push(text?.slice(lastIndex, match?.index));
      if (match?.[1]) {
        parts?.push(<em key={`italic-${match?.index}`} className="italic">{match?.[1]}</em>);
      } else if (match?.[2]) {
        parts?.push(<span key={`red-${match?.index}`} className="text-destructive">{match?.[2]}</span>);
      }
      lastIndex = match?.index + match?.[0]?.length;
    }
    if (lastIndex < text?.length) parts?.push(text?.slice(lastIndex));
    return parts?.length > 0 ? <>{parts}</> : text;
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s?.toString()?.padStart(2, '0')}`;
  };

  // Start session timer
  const startSessionTimer = useCallback(() => {
    const now = Date.now();
    setSessionStartTime(now);
    setCardStartTime(now);
    timerRef.current = setInterval(() => {
      setSessionElapsed(prev => prev + 1);
    }, 1000);
  }, []);

  const stopSessionTimer = useCallback(() => {
    if (timerRef?.current) {
      clearInterval(timerRef?.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopSessionTimer();
  }, [stopSessionTimer]);

  useEffect(() => {
    if (user?.id) {
      // If we have pre-loaded verses from verse-library Extra Review, use them directly
      if (incomingExtraReview && incomingVerses?.length > 0) {
        setTodaysReview(incomingVerses);
        setLoading(false);
      } else {
        loadTodaysReview();
      }
      loadMemorizationStats();
      loadPhase0Verses();

      // BUG 1: Run streak freeze check at the start of each session
      updateStreakWithFreezeCheck(user?.id)?.then(result => {
          if (result) {
            setStreakFreezeResult(result);
            // Surface result to user if gold was spent or streak was preserved after a gap
            if (result?.goldSpent > 0 || result?.newAwards?.length > 0) {
              setShowStreakFreezeNotice(true);
              result?.newAwards?.forEach(a => showAwardToast(a));
            }
          }
        })?.catch(err => console.warn('Streak freeze check failed:', err));
    }
  }, [user?.id, extraPracticeMode]);

  // Track session start when verses are loaded
  useEffect(() => {
    if (todaysReview?.length > 0 && !sessionComplete) {
      trackDailyReviewStarted(todaysReview?.length);
    }
  }, [todaysReview?.length]);

  // Start timer once verses are loaded
  useEffect(() => {
    if (!loading && todaysReview?.length > 0 && !sessionStartTime) {
      startSessionTimer();
    }
  }, [loading, todaysReview, sessionStartTime, startSessionTimer]);

  const loadTodaysReview = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    try {
      if (extraPracticeMode) {
        // Extra practice: get all user verses, then filter for overdue + tomorrow's due
        const tomorrow = getTomorrowLocalDateString();
        const today = getLocalDateString();

        // Fetch all user verses to find overdue and tomorrow's due
        const { data: allVerses, error: allError } = await verseService?.getUserVerses(user?.id);
        if (allError) throw new Error(allError?.message);

        const verses = allVerses || [];

        // Overdue: phase > 0 and nextReviewDate <= today
        const overdueVerses = verses?.filter(v =>
          v?.currentPhase > 0 &&
          v?.nextReviewDate &&
          v?.nextReviewDate <= today
        );

        // Tomorrow's due: nextReviewDate === tomorrow
        const tomorrowVerses = verses?.filter(v =>
          v?.currentPhase > 0 &&
          v?.nextReviewDate === tomorrow
        );

        // Deduplicate by id
        const seenIds = new Set(overdueVerses?.map(v => v?.id));
        const uniqueTomorrow = tomorrowVerses?.filter(v => !seenIds?.has(v?.id));

        const combined = [...overdueVerses, ...uniqueTomorrow];
        setTodaysReview(combined?.slice(0, 30));
      } else {
        const { data: verses, error: reviewError } = await verseService?.getVersesDueForReview(
          user?.id,
          getLocalDateString()
        );
        if (reviewError) throw new Error(reviewError.message);
        setTodaysReview(verses || []);
      }
    } catch (err) {
      console.error('Error loading today\'s review:', err);
      setError(err?.message || 'Failed to load verses for review');
      setTodaysReview([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMemorizationStats = async () => {
    if (!user?.id) return;
    try {
      const { data: stats, error: statsError } = await verseService?.getUserMemorizationStats(user?.id);
      if (statsError) { console.warn('Error loading memorization stats:', statsError); return; }
      setMemorizationStats(stats);
    } catch (err) {
      console.warn('Error loading memorization stats:', err);
    }
  };

  const loadPhase0Verses = async () => {
    if (!user?.id) return;
    try {
      const { data: verses, error: versesError } = await verseService?.getUserVerses(user?.id);
      if (versesError) { console.error('Error loading Phase 0 verses:', versesError); return; }
      const phase0Only = verses?.filter(v => v?.currentPhase === 0 || v?.current_phase === 0) || [];
      setPhase0Verses(phase0Only);
    } catch (err) {
      console.error('Error loading Phase 0 verses:', err);
    }
  };

  const handleStartPhase0Practice = () => {
    if (phase0Verses?.length === 0) {
      alert('No Phase 0 verses found. Add verses to Phase 0 to practice.');
      return;
    }
    navigate('/memorization-practice', { state: { verseQueue: phase0Verses, autoplay: true } });
  };

  const getBucketColor = (bucket) => {
    const colors = {
      daily: 'bg-destructive/15 text-destructive',
      weekly: 'bg-warning/15 text-warning',
      monthly: 'bg-primary/15 text-primary',
      quarterly: 'bg-success/15 text-success',
      memorization: 'bg-accent/15 text-accent-foreground',
      future: 'bg-accent/15 text-accent-foreground'
    };
    return colors?.[bucket?.toLowerCase()] || 'bg-muted text-muted-foreground';
  };

  const getPhaseText = (currentPhase, currentInterval) => {
    const interval = currentInterval && !isNaN(currentInterval) ? Math.round(currentInterval) : 0;
    return `Phase ${currentPhase || 1} (${interval}d)`;
  };

  // ── Trigger card animation helper ────────────────────────────────────────
  const triggerCardAnimation = useCallback((type, callback, delay = 600) => {
    setCardAnimation(type);
    setTimeout(() => {
      setCardAnimation(null);
      if (callback) callback();
    }, delay);
  }, []);

  const handleResponse = async (response) => {
    const currentVerse = todaysReview?.[currentVerseIndex];
    if (!currentVerse || !user?.id) return;

    // Record card time
    const now = Date.now();
    const cardTime = cardStartTime ? Math.round((now - cardStartTime) / 1000) : 0;
    const newCardTimes = [...cardTimes, cardTime];
    setCardTimes(newCardTimes);

    let newReviewSession = { ...reviewSession };
    newReviewSession?.completed?.push({ verse: currentVerse, response, timestamp: new Date() });

    if (!newReviewSession?.pendingUpdates) newReviewSession.pendingUpdates = [];
    newReviewSession?.pendingUpdates?.push({
      verseId: currentVerse?.id,
      response,
      verse: currentVerse,
      timestamp: new Date()
    });

    let difficulty = currentVerse?.currentPhase || 1;
    switch (response) {
      case 'good':
        newReviewSession.correct++;
        newReviewSession.totalDifficulty += difficulty;
        break;
      case 'hard':
        newReviewSession.correct++;
        newReviewSession.hard++;
        newReviewSession.totalDifficulty += difficulty + 1;
        break;
      case 'missed':
        newReviewSession.incorrect++;
        newReviewSession.missed++;
        if (!newReviewSession?.missedVerses) newReviewSession.missedVerses = [];
        newReviewSession?.missedVerses?.push(currentVerse);
        break;
    }

    // Record answer for this card index
    const newCardAnswers = { ...cardAnswers, [currentVerseIndex]: { response, timeSpent: cardTime } };
    setCardAnswers(newCardAnswers);

    // Update live progress
    setSessionProgress({
      reviewed: newReviewSession?.completed?.length,
      correct: newReviewSession?.correct,
      incorrect: newReviewSession?.incorrect,
      hard: newReviewSession?.hard,
    });

    setReviewSession(newReviewSession);

    const isLast = currentVerseIndex >= todaysReview?.length - 1;

    // Animate then advance
    if (response === 'missed') {
      triggerHaptic('heavy');
      triggerCardAnimation('shake', () => {
        if (!isLast) {
          setCurrentVerseIndex(currentVerseIndex + 1);
          setShowVerse(false);
          setCardStartTime(Date.now());
        } else {
          stopSessionTimer();
          setSessionComplete(true);
          setShowCelebration(true);
          processBatchUpdates(newReviewSession?.pendingUpdates);
          trackDailyReviewCompleted(
            newReviewSession?.completed?.length || todaysReview?.length,
            sessionElapsed
          );
        }
      }, 650);
    } else if (response === 'good') {
      triggerHaptic('light');
      triggerCardAnimation('glow', () => {
        if (!isLast) {
          setCurrentVerseIndex(currentVerseIndex + 1);
          setShowVerse(false);
          setCardStartTime(Date.now());
        } else {
          stopSessionTimer();
          setSessionComplete(true);
          setShowCelebration(true);
          processBatchUpdates(newReviewSession?.pendingUpdates);
          trackDailyReviewCompleted(
            newReviewSession?.completed?.length || todaysReview?.length,
            sessionElapsed
          );
        }
      }, 500);
    } else {
      // hard — advance immediately
      if (!isLast) {
        setCurrentVerseIndex(currentVerseIndex + 1);
        setShowVerse(false);
        setCardStartTime(Date.now());
      } else {
        stopSessionTimer();
        setSessionComplete(true);
        setShowCelebration(true);
        await processBatchUpdates(newReviewSession?.pendingUpdates);
        trackDailyReviewCompleted(
          newReviewSession?.completed?.length || todaysReview?.length,
          sessionElapsed
        );
      }
    }
  };

  // Keep ref in sync with latest handleResponse
  handleResponseRef.current = handleResponse;

  const processBatchUpdates = async (pendingUpdates) => {
    if (!pendingUpdates || pendingUpdates?.length === 0) return;
    setIsBatchProcessing(true);
    try {
      // For extra review from verse-library, use reschedule preference
      const shouldReschedule = extraPracticeMode ? extraReviewReschedule : true;
      const shouldUpdateLastReviewed = extraPracticeMode ? extraReviewUpdateLastReviewed : true;
      const sessionType = extraPracticeMode ? 'extra_practice' : (showMemorization ? 'memorization' : 'daily_review');

      // Collect phase change info for VERSE_MASTERED award
      let phasedVerses = [];

      if (shouldReschedule) {
        // Use optimized batch handler (single verse fetch + single schedule map fetch)
        // This also updates last_reviewed_at as part of spaced repetition
        const batchResult = await verseService?.batchHandleReviewResponsesOptimized(pendingUpdates, sessionType);

        // Extract phase changes from batch results for award evaluation
        if (batchResult?.results?.length) {
          phasedVerses = batchResult?.results
            ?.filter(r => r?.phase_changed)
            ?.map(r => ({ oldPhase: r?.phase_before, newPhase: r?.phase_after }));
        }
      } else if (shouldUpdateLastReviewed) {
        // Reschedule is off but user wants last_reviewed_at updated — bulk-stamp today's date
        const now = new Date()?.toISOString();
        const verseIds = pendingUpdates?.map(u => u?.verseId)?.filter(Boolean);
        if (verseIds?.length > 0) {
          try {
            await verseService?.bulkUpdateVerses(verseIds, { last_reviewed_at: now });
          } catch (bulkErr) {
            console.warn('Bulk last_reviewed_at update failed, falling back to individual updates:', bulkErr);
            // Fallback: update each verse individually
            await Promise.allSettled(
              verseIds?.map(id => verseService?.updateVerse(id, { last_reviewed_at: now }))
            );
          }
        }
      }
      // else: both toggles off — skip all DB updates

      // Log activities in parallel
      const activityPromises = pendingUpdates?.map(async (update, i) => {
        try {
          await activityService?.logActivity(user?.id, {
            type: 'practiced',
            title: `Reviewed ${update?.verse?.reference}`,
            description: `Response: ${update?.response}`,
            verseId: update?.verseId,
            metadata: {
              response: update?.response,
              reviewSuccess: update?.response === 'good' || update?.response === 'hard',
              phase: update?.verse?.currentPhase,
              sessionType
            }
          });
        } catch (err) {
          console.warn('Activity log failed for verse:', update?.verseId);
        }
      });
      await Promise.allSettled(activityPromises);

      // Award checks on session completion
      if (user?.id) {
        try {
          const correctCount = pendingUpdates?.filter(u => u?.response === 'good' || u?.response === 'hard')?.length || 0;
          const startHour = sessionStartTime ? new Date(sessionStartTime)?.getHours() : new Date()?.getHours();
          const newAwards = await recordReviewSession(user?.id, {
            verse_count: pendingUpdates?.length,
            correct_count: correctCount,
            duration_seconds: sessionElapsed,
            start_hour: startHour,
            phasedVerses,
          });
          newAwards?.forEach(a => showAwardToast(a));
        } catch (awardErr) {
          console.warn('Award check failed:', awardErr);
        }
      }

      if (!extraPracticeMode && !showMemorization) {
        const today = getLocalDateString();
        localStorage.setItem('lastDailyReviewCompleted', today);
        setDailyReviewCompleted(true);
      }
    } catch (err) {
      console.error('Error in batch processing:', err);
      setError('Some updates failed to save. Please check your connection.');
    } finally {
      setIsBatchProcessing(false);
    }
  };

  const handleMemorizationContinue = () => {
    setShowMemorization(true);
    setSessionComplete(false);
    setCurrentVerseIndex(0);
    setShowVerse(false);
    setCardAnswers({});
    setCardTimes([]);
    setSessionElapsed(0);
    setReviewSession({ completed: [], correct: 0, incorrect: 0, hard: 0, missed: 0, totalDifficulty: 0, missedVerses: [] });
    setSessionProgress({ reviewed: 0, correct: 0, incorrect: 0, hard: 0 });
    setTodaysReview(phase0Verses);
  };

  const handleExtraPractice = () => {
    setExtraPracticeMode(true);
    setSessionComplete(false);
    setCurrentVerseIndex(0);
    setShowVerse(false);
    setCardAnswers({});
    setCardTimes([]);
    setSessionElapsed(0);
    setReviewSession({ completed: [], correct: 0, incorrect: 0, hard: 0, missed: 0, totalDifficulty: 0, missedVerses: [] });
    setSessionProgress({ reviewed: 0, correct: 0, incorrect: 0, hard: 0 });
    loadTodaysReview();
  };

  const moveToBucket = async (targetBucket) => {
    const currentVerse = todaysReview?.[currentVerseIndex];
    if (!currentVerse || !user?.id) return;
    try {
      const updates = { memorization_bucket: targetBucket };
      if (targetBucket === 'daily') {
        updates.current_phase = 2;
        updates.current_interval = 1.0;
        updates.next_review_date = new Date()?.toISOString()?.split('T')?.[0];
      }
      const { error: updateError } = await verseService?.updateVerse(currentVerse?.id, updates);
      if (updateError) console.error('Error updating verse bucket:', updateError);
      else {
        await activityService?.logActivity(user?.id, {
          type: 'verse_added',
          title: `Moved ${currentVerse?.reference} to ${targetBucket}`,
          description: `Bucket changed from memorization to ${targetBucket}`,
          verseId: currentVerse?.id,
          metadata: { previousBucket: 'memorization', newBucket: targetBucket }
        });
      }
    } catch (err) {
      console.error('Error moving verse to bucket:', err);
    }
    if (currentVerseIndex < todaysReview?.length - 1) {
      setCurrentVerseIndex(currentVerseIndex + 1);
      setShowVerse(false);
      setCardStartTime(Date.now());
    } else {
      stopSessionTimer();
      setSessionComplete(true);
    }
  };

  const playAudio = (text) => {
    if (settings?.audioEnabled && 'speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      speechSynthesis.speak(utterance);
    }
  };

  const handleReflectOnVerse = () => {
    const currentVerse = todaysReview?.[currentVerseIndex];
    if (currentVerse) {
      setSelectedVerseForReflection(currentVerse);
      setIsReflectionModalOpen(true);
    }
  };

  const handleSaveReflection = async (reflection) => {
    try {
      console.log('Reflection saved during daily review:', reflection);
    } catch (err) {
      console.error('Error saving reflection:', err);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event?.target?.tagName === 'INPUT' || event?.target?.tagName === 'TEXTAREA') return;
      if (sessionComplete || showMemorization) return;

      if (event?.code === 'Space' || event?.key === ' ') {
        event?.preventDefault();
        if (!showVerse) {
          setShowVerse(true);
        } else {
          handleResponse('good');
        }
      } else if (showVerse) {
        if (event?.key === '1') { event?.preventDefault(); handleResponse('missed'); }
        else if (event?.key === '2') { event?.preventDefault(); handleResponse('hard'); }
        else if (event?.key === '3') { event?.preventDefault(); handleResponse('good'); }
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showVerse, sessionComplete, showMemorization, currentVerseIndex, todaysReview, reviewSession, user?.id]);

  const currentVerse = todaysReview?.[currentVerseIndex];
  const progress = todaysReview?.length > 0 ? ((currentVerseIndex + (sessionComplete ? 1 : 0)) / todaysReview?.length) * 100 : 0;

  // Compute live progress stats
  const totalReviewed = sessionProgress?.reviewed;
  const successRate = totalReviewed > 0 ? Math.round(((sessionProgress?.correct) / totalReviewed) * 100) : 0;
  const overdueCount = todaysReview?.filter(v => v?.isOverdue)?.length || 0;

  // ── Session progress bar segments ─────────────────────────────────────────
  const totalVerses = todaysReview?.length || 1;
  const missedPct = (sessionProgress?.incorrect / totalVerses) * 100;
  const hardPct = (sessionProgress?.hard / totalVerses) * 100;
  const goodPct = ((sessionProgress?.correct - sessionProgress?.hard) / totalVerses) * 100;

  // Navigation rules: can navigate to answered cards and the next unanswered card
  const canNavigateTo = (index) => {
    if (index < 0 || index >= todaysReview?.length) return false;
    // Can always go to an answered card
    if (cardAnswers?.[index] !== undefined) return true;
    // Can go to the first unanswered card (current frontier)
    const firstUnanswered = todaysReview?.findIndex((_, i) => cardAnswers?.[i] === undefined);
    return index === firstUnanswered;
  };

  const canGoBack = currentVerseIndex > 0 && canNavigateTo(currentVerseIndex - 1);
  const canGoForward = currentVerseIndex < todaysReview?.length - 1 && canNavigateTo(currentVerseIndex + 1);
  const currentCardAnswered = cardAnswers?.[currentVerseIndex] !== undefined;

  // Format last reviewed date
  const formatLastReviewed = (dateStr) => {
    if (!dateStr) return 'Never';
    const d = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return `${Math.floor(diffDays / 30)}mo ago`;
  };

  // Session end stats
  const sessionTotalTime = sessionElapsed;
  const avgTimePerCard = cardTimes?.length > 0 ? Math.round(cardTimes?.reduce((a, b) => a + b, 0) / cardTimes?.length) : 0;
  const longestCard = cardTimes?.length > 0 ? Math.max(...cardTimes) : 0;
  const shortestCard = cardTimes?.length > 0 ? Math.min(...cardTimes) : 0;

  // ── Card animation CSS class ──────────────────────────────────────────────
  const cardAnimationClass = cardAnimation === 'shake' ?'animate-card-shake'
    : cardAnimation === 'glow' ?'animate-card-glow' :'';

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header title="Daily Review" />
        <DailyReviewSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header title="Daily Review" />
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-surface rounded-lg shadow-sm border border-border p-8 text-center">
            <div className="w-16 h-16 bg-destructive/15 rounded-full flex items-center justify-center mx-auto mb-4">
              <RotateCcw className="w-8 h-8 text-destructive" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Unable to Load Review</h2>
            <p className="text-muted-foreground mb-8">{error}</p>
            <Button onClick={() => { setError(''); loadTodaysReview(); }} className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (todaysReview?.length === 0 && !loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header title={extraPracticeMode ? "Extra Practice" : showMemorization ? "Memorization Session" : "Daily Review"} />
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-surface rounded-lg shadow-sm border border-border p-8 text-center">
            <div className="w-16 h-16 bg-success/15 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {extraPracticeMode ? "No Extra Practice Needed" : showMemorization ? "No Memorization Verses" : "No Reviews Due Today"}
            </h2>
            <p className="text-muted-foreground mb-8">
              {extraPracticeMode ? "You don't have any overdue or upcoming verses to practice." :
               showMemorization ? "You don't have any verses in your memorization queue." : "You're all caught up! No verses are due for review today."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate('/verse-library')} className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3">
                Add More Verses
              </Button>
              <Button onClick={() => navigate('/verse-library')} variant="outline" className="px-6 py-3">
Return to Verse Library
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (sessionComplete && !showMemorization) {
    const avgDifficulty = reviewSession?.completed?.length > 0
      ? (reviewSession?.totalDifficulty / reviewSession?.completed?.length)?.toFixed(1)
      : 0;
    const hasMissedVerses = reviewSession?.missedVerses?.length > 0;
    const hasMemorizationVerses = memorizationStats?.phase1Count > 0;
    const canDoExtraPractice = !extraPracticeMode && hasMissedVerses;
    const finalSuccessRate = reviewSession?.completed?.length > 0
      ? Math.round((reviewSession?.correct / reviewSession?.completed?.length) * 100)
      : 0;

    return (
      <div className="min-h-screen bg-background">
        {/* Celebration overlay */}
        {showCelebration && (
          <CelebrationOverlay onDone={() => setShowCelebration(false)} />
        )}
        <Header title={extraPracticeMode ? "Extra Practice Complete" : "Daily Review Complete"} />
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-surface rounded-lg shadow-sm border border-border p-8 text-center">
            {isBatchProcessing ? (
              <>
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Saving Your Progress...</h2>
                <p className="text-muted-foreground mb-8">
                  Processing {reviewSession?.pendingUpdates?.length} verse reviews and updating schedules.
                </p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-success/15 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-success" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  {extraPracticeMode ? "Extra Practice Complete!" : "Excellent Work!"}
                </h2>
                <p className="text-muted-foreground mb-8">
                  {extraPracticeMode
                    ? "You've completed your extra practice session."
                    : "You've completed today's scheduled review session."}
                </p>
              </>
            )}

            {/* Session Results */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-success/10 border border-success/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-success">{reviewSession?.correct}</div>
                <div className="text-sm text-success">Correct</div>
              </div>
              <div className="bg-destructive/10 border border-destructive/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-destructive">{reviewSession?.incorrect}</div>
                <div className="text-sm text-destructive">Missed</div>
              </div>
              <div className="bg-warning/10 border border-warning/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-warning">{reviewSession?.hard}</div>
                <div className="text-sm text-warning">Hard</div>
              </div>
              <div className="bg-primary/10 border border-primary/20 p-4 rounded-lg">
                <div className="text-2xl font-bold text-primary">{finalSuccessRate}%</div>
                <div className="text-sm text-primary">Success Rate</div>
              </div>
            </div>

            {/* Time Statistics */}
            <div className="bg-muted/40 rounded-lg p-5 mb-6 text-left">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Timer className="w-4 h-4 text-primary" />
                Session Time Statistics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Total Time</div>
                  <div className="text-lg font-bold text-foreground">{formatTime(sessionTotalTime)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Avg per Card</div>
                  <div className="text-lg font-bold text-foreground">{formatTime(avgTimePerCard)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Longest Card</div>
                  <div className="text-lg font-bold text-foreground">{formatTime(longestCard)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Shortest Card</div>
                  <div className="text-lg font-bold text-foreground">{formatTime(shortestCard)}</div>
                </div>
              </div>
            </div>

            {canDoExtraPractice && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Plus className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-semibold text-foreground">Extra Practice Available</h3>
                </div>
                <p className="text-muted-foreground mb-4">Continue practicing with up to 30 additional verses.</p>
                <Button onClick={handleExtraPractice} className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 mr-4">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Start Extra Practice (30 verses)
                </Button>
              </div>
            )}

            {!extraPracticeMode && hasMemorizationVerses && (
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <BookOpen className="w-5 h-5 text-accent" />
                  <h3 className="text-lg font-semibold text-foreground">Phase 0 Memorization Practice</h3>
                </div>
                <p className="text-muted-foreground mb-4">
                  Practice memorizing {phase0Verses?.length} Phase 0 verse{phase0Verses?.length !== 1 ? 's' : ''} using chunking.
                </p>
                <Button onClick={handleMemorizationContinue} className="bg-accent hover:bg-accent/90 text-accent-foreground px-6 py-3">
                  Continue with Phase 0 Memorization Practice
                </Button>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate('/verse-library')} variant="outline" className="px-6 py-3">
Return to Verse Library
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Progress and Settings Bar
  return (
    <div className="min-h-screen bg-background">
      {/* Inline animation styles */}
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes cardShake {
          0%, 100% { transform: translateX(0); }
          10% { transform: translateX(-10px) rotate(-1deg); }
          20% { transform: translateX(10px) rotate(1deg); }
          30% { transform: translateX(-10px) rotate(-1deg); }
          40% { transform: translateX(10px) rotate(1deg); }
          50% { transform: translateX(-8px); }
          60% { transform: translateX(8px); }
          70% { transform: translateX(-5px); }
          80% { transform: translateX(5px); }
          90% { transform: translateX(-2px); }
        }
        @keyframes cardGlow {
          0% { box-shadow: 0 0 0 0 rgba(16,185,129,0); border-color: transparent; }
          30% { box-shadow: 0 0 0 6px rgba(16,185,129,0.35), 0 0 24px rgba(16,185,129,0.25); border-color: rgba(16,185,129,0.7); }
          60% { box-shadow: 0 0 0 10px rgba(16,185,129,0.2), 0 0 40px rgba(16,185,129,0.15); border-color: rgba(16,185,129,0.5); }
          100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); border-color: transparent; }
        }
        @keyframes bounceIn {
          0% { transform: scale(0.5); opacity: 0; }
          60% { transform: scale(1.1); opacity: 1; }
          80% { transform: scale(0.95); }
          100% { transform: scale(1); }
        }
        .animate-card-shake {
          animation: cardShake 0.6s ease-in-out;
          border-color: rgba(239,68,68,0.6) !important;
          background-color: rgba(239,68,68,0.05) !important;
        }
        .animate-card-glow {
          animation: cardGlow 0.5s ease-out;
        }
        .animate-bounce-in {
          animation: bounceIn 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97);
        }
        .verse-text-clickable {
          cursor: pointer;
          transition: background-color 0.15s ease;
        }
        .verse-text-clickable:hover {
          background-color: rgba(16,185,129,0.08);
          border-radius: 8px;
        }
        .verse-text-clickable:active {
          background-color: rgba(16,185,129,0.15);
        }
      `}</style>
      <Header title={extraPracticeMode ? "Extra Practice Session" : showMemorization ? "Memorization Session" : "Daily Review"} />
      <FadeSlideIn>
      <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-6 pb-24 sm:pb-6">

        {/* Streak Freeze Notice */}
        {showStreakFreezeNotice && streakFreezeResult && (
          <div className="bg-warning/10 border border-warning/30 rounded-lg p-3 mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-warning">
              <span className="text-lg">🔥</span>
              {streakFreezeResult?.goldSpent > 0 ? (
                <span>
                  <strong>{streakFreezeResult?.goldSpent} gold</strong> spent to preserve your streak! Streak: <strong>{streakFreezeResult?.newStreak} days</strong>
                </span>
              ) : (
                <span>Streak active: <strong>{streakFreezeResult?.newStreak} days</strong></span>
              )}
            </div>
            <button
              onClick={() => setShowStreakFreezeNotice(false)}
              className="text-warning/60 hover:text-warning transition-colors flex-shrink-0"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {/* Session Timer Bar */}
        <div className="bg-surface rounded-lg shadow-sm border border-border px-3 sm:px-4 py-2 sm:mb-4 flex items-center justify-between mb-[-3px]">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Timer className="w-4 h-4 text-primary shrink-0" />
            <span className="font-mono font-medium text-foreground">{formatTime(sessionElapsed)}</span>
            <span className="hidden sm:inline">session time</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 text-xs text-muted-foreground">
            <span>{sessionProgress?.reviewed} reviewed</span>
            {sessionProgress?.reviewed > 0 && (
              <span className="text-success font-medium">{successRate}% success</span>
            )}
          </div>
        </div>

        {extraPracticeMode && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 text-primary">
              <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
              <span className="font-medium text-sm sm:text-base">Extra Practice Mode — Up to 30 Verses</span>
            </div>
          </div>
        )}

        {showMemorization && (
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 text-accent">
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
              <span className="font-medium text-sm sm:text-base">Memorization Session</span>
            </div>
          </div>
        )}

        <div className="bg-surface rounded-lg shadow-sm border border-border p-3 sm:p-4 sm:mb-6 mb-0.5 py-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-3 sm:mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Settings className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  {extraPracticeMode ? 'Extra: 30 max' : 'Daily: 30 max'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Text Size Control — −/T/+ */}
              <div className="flex items-center border border-border rounded-md overflow-hidden bg-background">
                <button
                  onClick={handleTextSizeDecrease}
                  disabled={currentTextSizeIndex === 0}
                  title="Decrease text size"
                  className="px-2.5 py-2 text-sm font-bold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed min-h-[44px] min-w-[36px]"
                >
                  −
                </button>
                <span
                  className="px-2 font-bold text-foreground select-none flex items-center justify-center min-w-[32px] min-h-[44px]"
                  style={{ fontSize: textSizeScaleMap?.[textSize] || '1rem', lineHeight: 1 }}
                >
                  T
                </span>
                <button
                  onClick={handleTextSizeIncrease}
                  disabled={currentTextSizeIndex === TEXT_SIZES?.length - 1}
                  title="Increase text size"
                  className="px-2.5 py-2 text-sm font-bold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed min-h-[44px] min-w-[36px]"
                >
                  +
                </button>
              </div>
              <Button onClick={() => setBulkReview(!bulkReview)} variant={bulkReview ? "default" : "outline"} size="sm" className="min-h-[44px] text-xs sm:text-sm px-3 sm:px-4">
                Bulk Review
              </Button>
              <Button onClick={() => playAudio(currentVerse?.text || currentVerse?.reference || '')} variant="outline" size="sm" disabled={!settings?.audioEnabled} className="min-h-[44px] min-w-[44px]">
                <Volume2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Session Progress Bar */}
          <div className="mb-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>{sessionProgress?.reviewed} of {todaysReview?.length} verses</span>
              <div className="flex items-center gap-2">
                {sessionProgress?.incorrect > 0 && <span className="text-destructive font-medium">{sessionProgress?.incorrect} missed</span>}
                {sessionProgress?.hard > 0 && <span className="text-warning font-medium">{sessionProgress?.hard} hard</span>}
                {(sessionProgress?.correct - sessionProgress?.hard) > 0 && <span className="text-success font-medium">{sessionProgress?.correct - sessionProgress?.hard} good</span>}
              </div>
            </div>
            <div className="w-full h-3 bg-muted rounded-full overflow-hidden flex">
              {/* Missed — red */}
              {missedPct > 0 && (
                <div
                  className="h-full bg-destructive transition-all duration-500"
                  style={{ width: `${missedPct}%` }}
                />
              )}
              {/* Hard — orange */}
              {hardPct > 0 && (
                <div
                  className="h-full bg-warning transition-all duration-500"
                  style={{ width: `${hardPct}%` }}
                />
              )}
              {/* Good — green */}
              {goodPct > 0 && (
                <div
                  className="h-full bg-success transition-all duration-500"
                  style={{ width: `${goodPct}%` }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="bg-surface border border-border rounded-xl shadow-2xl p-6 max-w-sm w-full">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-destructive/15 rounded-full flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5 text-destructive" />
                </div>
                <h3 className="text-lg font-bold text-foreground">Delete Verse?</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                <strong className="text-foreground">{currentVerse?.reference}</strong> will be permanently removed from your library.
              </p>
              <p className="text-xs text-destructive/80 bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2 mb-5">
                ⚠️ This action cannot be undone. All review history for this verse will be lost.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowDeleteConfirm(false)}
                  variant="outline"
                  className="flex-1 min-h-[44px]"
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteVerse}
                  className="flex-1 min-h-[44px] bg-destructive hover:bg-destructive/90 text-white"
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting…' : 'Delete Verse'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Main Review Card */}
        {currentVerse && (
          <>
            {/* Delete button — outside the card */}
            <div className="flex justify-end mb-1.5">
              <button
                onClick={(e) => { e?.stopPropagation(); setShowDeleteConfirm(true); }}
                title="Delete this verse from library"
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors px-2 py-1 rounded-md hover:bg-destructive/10"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Delete verse</span>
              </button>
            </div>
          <div
            className={`bg-surface rounded-xl shadow-sm border overflow-hidden mb-4 sm:mb-6 transition-all duration-200 select-none ${cardAnimationClass} ${
              swipeHint === 'left' ?'border-destructive/60 bg-destructive/5 -translate-x-2'
                : swipeHint === 'right'?'border-success/60 bg-success/5 translate-x-2' :'border-border'
            }`}
            {...swipeHandlers}
            onClick={handleCardTap}
          >
            {/* Swipe hint overlay */}
            {swipeHint && (
              <div className={`absolute inset-0 z-10 flex items-center justify-center rounded-xl pointer-events-none ${
                swipeHint === 'left' ? 'bg-destructive/10' : 'bg-success/10'
              }`}>
                <span className={`text-2xl font-bold ${
                  swipeHint === 'left' ? 'text-destructive' : 'text-success'
                }`}>
                  {swipeHint === 'left' ? '✗ Missed' : '✓ Good'}
                </span>
              </div>
            )}
            <div className="p-4 sm:p-6 lg:p-8 pt-0 pb-px">
              {/* Card header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  {currentVerse?.isOverdue && (
                    <span className="text-xs text-destructive font-medium bg-destructive/10 px-2 py-1.5 rounded-md">
                      ⚠️ Overdue {Math.abs(currentVerse?.daysUntilReview)}d
                    </span>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span className="font-medium">{getPhaseText(currentVerse?.currentPhase, currentVerse?.currentInterval)}</span>
                  </div>
                  {showVerse && currentCardAnswered === false && (
                    <>
                      <span>Last: {formatLastReviewed(currentVerse?.lastReviewedAt || currentVerse?.last_reviewed_at)}</span>
                      {(currentVerse?.successRate !== undefined || currentVerse?.success_rate !== undefined) && (
                        <span>
                          Success: {Math.round((currentVerse?.successRate ?? currentVerse?.success_rate ?? 0) * 100)}%
                        </span>
                      )}
                    </>
                  )}
                  {currentCardAnswered && (
                    <>
                      <span>Last: {formatLastReviewed(currentVerse?.lastReviewedAt || currentVerse?.last_reviewed_at)}</span>
                      {(currentVerse?.successRate !== undefined || currentVerse?.success_rate !== undefined) && (
                        <span>
                          Success: {Math.round((currentVerse?.successRate ?? currentVerse?.success_rate ?? 0) * 100)}%
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>

              {!showVerse ? (
                <div className="text-center py-8 sm:py-12">
                  <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-6 sm:mb-8">{currentVerse?.reference}</h2>
                  {/* Mobile swipe hint */}
                  <p className="text-xs text-muted-foreground mb-5 sm:hidden">Tap card to reveal • Swipe to answer</p>

                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      onClick={(e) => { e?.stopPropagation(); triggerHaptic('light'); setShowVerse(true); }}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 sm:px-8 py-3 min-h-[52px] text-base active:scale-95 transition-transform w-full sm:w-auto"
                    >
                      Reveal Verse
                    </Button>
                    <Button
                      onClick={(e) => { e?.stopPropagation(); handleReflectOnVerse(); }}
                      variant="outline"
                      className="px-5 sm:px-6 py-3 min-h-[52px] active:scale-95 transition-transform w-full sm:w-auto"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Reflect on Verse
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <div
                    className="bg-muted/40 p-4 sm:p-6 rounded-xl sm:mb-6 verse-text-clickable mb-1.5 pt-2"
                    onClick={(e) => {
                      e?.stopPropagation();
                      if (!currentCardAnswered && !showMemorization) {
                        triggerHaptic('light');
                        handleResponse('good');
                      }
                    }}
                    title={!currentCardAnswered && !showMemorization ? "Click to mark as Good ✓" : undefined}
                  >
                    {currentVerse?.text ? (
                      <>
                        <p className="leading-relaxed text-foreground" style={{ fontSize: getTextSizePx(), lineHeight: '1.85' }}>
                          "{formatKJVText(currentVerse?.text)}"
                        </p>
                        <p className="text-right text-muted-foreground mt-3 sm:mt-4 font-medium" style={{ fontSize: getTextSizePx() }}>
                          — {currentVerse?.reference}
                        </p>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-muted-foreground mb-4">Unable to load verse text</p>
                        <p className="text-sm text-muted-foreground">Reference: {currentVerse?.reference}</p>
                      </div>
                    )}
                  </div>

                  {/* Review Action Buttons */}
                  {!showMemorization ? (
                    <div className="space-y-3">
                      <div className="text-center mb-3">
                        <p className="text-sm font-medium text-foreground">How well did you recall this verse?</p>
                        {/* Mobile swipe hint */}
                        <p className="text-xs text-muted-foreground mt-1 sm:hidden">← Missed &nbsp;|&nbsp; Good →</p>
                      </div>
                      <div className="grid grid-cols-3 gap-2 sm:gap-3">
                        <Button
                          onClick={(e) => { e?.stopPropagation(); handleResponse('missed'); }}
                          className="px-2 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-medium min-h-[52px] active:scale-95 transition-transform text-white"
                          style={{ backgroundColor: '#EF4444' }}
                        >
                          Missed
                        </Button>
                        <Button
                          onClick={(e) => { e?.stopPropagation(); triggerHaptic('medium'); handleResponse('hard'); }}
                          className="bg-warning hover:bg-warning/90 text-warning-foreground px-2 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-medium min-h-[52px] active:scale-95 transition-transform"
                        >
                          Hard
                        </Button>
                        <Button
                          onClick={(e) => { e?.stopPropagation(); triggerHaptic('light'); handleResponse('good'); }}
                          className="bg-success hover:bg-success/90 text-success-foreground px-2 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-medium min-h-[52px] active:scale-95 transition-transform"
                        >
                          Good
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button onClick={() => moveToBucket('daily')} className="bg-success hover:bg-success/90 text-success-foreground px-4 sm:px-6 py-3 flex-1 min-h-[52px] active:scale-95 transition-transform">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Promote to Daily Review
                      </Button>
                      <Button
                        onClick={() => {
                          if (currentVerseIndex < todaysReview?.length - 1) {
                            setCurrentVerseIndex(currentVerseIndex + 1);
                            setShowVerse(false);
                            setCardStartTime(Date.now());
                          } else {
                            stopSessionTimer();
                            setSessionComplete(true);
                          }
                        }}
                        variant="outline"
                        className="px-4 sm:px-6 py-3 flex-1 min-h-[52px] active:scale-95 transition-transform"
                      >
                        Keep in Memorization
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          </>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center sm:mb-6 mt-[-15px] mb-1">
          <Button
            onClick={() => {
              if (canGoBack) {
                setCurrentVerseIndex(currentVerseIndex - 1);
                setShowVerse(cardAnswers?.[currentVerseIndex - 1] !== undefined);
                setCardStartTime(Date.now());
              }
            }}
            variant="outline"
            disabled={!canGoBack}
            className="flex items-center gap-1.5 sm:gap-2 min-h-[48px] px-4 sm:px-5"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">Prev</span>
          </Button>

          <div className="text-xs sm:text-sm text-muted-foreground text-center px-2">
            <span className="hidden sm:inline">{sessionProgress?.reviewed} answered • {todaysReview?.length - sessionProgress?.reviewed} remaining</span>
            <span className="sm:hidden">{sessionProgress?.reviewed}/{todaysReview?.length}</span>
          </div>

          <Button
            onClick={() => {
              if (canGoForward) {
                setCurrentVerseIndex(currentVerseIndex + 1);
                setShowVerse(cardAnswers?.[currentVerseIndex + 1] !== undefined);
                setCardStartTime(Date.now());
              }
            }}
            variant="outline"
            disabled={!canGoForward}
            className="flex items-center gap-1.5 sm:gap-2 min-h-[48px] px-4 sm:px-5"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Your Progress — live stats */}
        <div className="bg-surface rounded-lg shadow-sm border border-border p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Your Progress</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
            <div className="bg-primary/10 border border-primary/20 p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm font-medium text-primary">Due Today</div>
              <div className="text-xl sm:text-2xl font-bold text-primary">{todaysReview?.length}</div>
              <div className="text-xs text-primary/70">
                {sessionProgress?.reviewed} reviewed
              </div>
            </div>
            <div className="bg-destructive/10 border border-destructive/20 p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm font-medium text-destructive">Overdue</div>
              <div className="text-xl sm:text-2xl font-bold text-destructive">{overdueCount}</div>
              <div className="text-xs text-destructive/70">
                {sessionProgress?.incorrect} missed
              </div>
            </div>
            <div className="bg-success/10 border border-success/20 p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm font-medium text-success">Success Rate</div>
              <div className="text-xl sm:text-2xl font-bold text-success">
                {sessionProgress?.reviewed > 0 ? `${successRate}%` : '—'}
              </div>
              <div className="text-xs text-success/70">
                {sessionProgress?.correct} correct
              </div>
            </div>
            <div className="bg-accent/10 border border-accent/20 p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm font-medium text-accent">Total Verses</div>
              <div className="text-xl sm:text-2xl font-bold text-accent">{memorizationStats?.totalVerses || 0}</div>
              <div className="text-xs text-accent/70">in library</div>
            </div>
          </div>
        </div>

        {settings?.maxDailyVerses < 30 && (
          <div className="mt-4 sm:mt-6 bg-warning/10 border border-warning/20 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 text-warning">
              <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
              <span className="font-medium text-sm sm:text-base">Review Capacity Warning</span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Your current setting of {settings?.maxDailyVerses} verses per day may not be sufficient for optimal spaced repetition.
              Consider increasing to at least 30 verses daily for better retention.
            </p>
          </div>
        )}
      </div>
      </FadeSlideIn>
      <VerseReflectionModal
        verse={selectedVerseForReflection}
        isOpen={isReflectionModalOpen}
        onClose={() => { setIsReflectionModalOpen(false); setSelectedVerseForReflection(null); }}
        onSave={handleSaveReflection}
      />
    </div>
  );
};

export default DailyReviewSystem;